import React, { useEffect, useState } from "react";
import { Breadcrumb, Layout, Tabs, Card, Typography, Tag, Button, Space, Spin, Descriptions } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useParams, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import { GeneralStatusEnum } from "../../enums/GeneralStatusEnum";
import { IEvent } from "../../services/nestjs-base-api/types";
import { useGetAllEventsQuery } from "../../services/nestjs-base-api/admin-api";
import { ArrowLeftOutlined, CalendarOutlined, PictureOutlined, EditOutlined, RobotOutlined, BarChartOutlined } from "@ant-design/icons";
import { Utils } from "../../utils/utils";
import RecognitionIndexingStatus from "../../components/RecognitionIndexingStatus";
import EventAnalytics from "../../components/EventAnalytics";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Paragraph } = Typography;

const AdminEventDetailPage: React.FunctionComponent = () => {
  const intl = useIntl();
  const { user, logout } = useAuth();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("details");

  // Fetch events data to find the specific event
  const { data, error, isLoading } = useGetAllEventsQuery({ 
    jwtToken: user.jwtToken 
  }, { pollingInterval: 600000 });

  useEffect(() => {
    if (error) {
      if (error && 'status' in error) {
        if (error.status === 401) {
          logout();
        }
      }
    }
  }, [error, logout]);

  // Find the specific event by ID
  const event = data?.find((e: IEvent) => e.id === parseInt(eventId || '0'));

  // Function to format date
  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMMM D, YYYY');
  };

  // Handle tab change
  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  // Function to get status color
  const getStatusColor = (status: GeneralStatusEnum) => {
    return status === GeneralStatusEnum.Active ? 'green' : 'red';
  };

  if (isLoading) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate('/admin/events')} className="breadcrumb-link">
            <FormattedMessage id={'section.eventsName'} defaultMessage="Events" />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'admin.event.details'} defaultMessage="Event Details" />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div
          style={{
            padding: 24,
            minHeight: '79vh',
            background: 'white',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" />
        </div>
      </Content>
    );
  }

  if (!event) {
    return (
      <Content style={{ margin: '0 16px' }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
          </Breadcrumb.Item>
          <Breadcrumb.Item onClick={() => navigate('/admin/events')} className="breadcrumb-link">
            <FormattedMessage id={'section.eventsName'} defaultMessage="Events" />
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <FormattedMessage id={'admin.event.details'} defaultMessage="Event Details" />
          </Breadcrumb.Item>
        </Breadcrumb>
        <div
          style={{
            padding: 24,
            minHeight: '79vh',
            background: 'white',
            borderRadius: 2,
          }}
        >
          <div style={{ marginBottom: 16 }}>
            <Button 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate('/admin/events')}
            >
              <FormattedMessage id={'common.back'} defaultMessage="Back to Events" />
            </Button>
          </div>
          <Card>
            <Title level={4}>
              <FormattedMessage id={'admin.event.notFound'} defaultMessage="Event not found" />
            </Title>
            <Paragraph>
              <FormattedMessage 
                id={'admin.event.notFoundDesc'} 
                defaultMessage="The event you are looking for does not exist or you don't have permission to view it." 
              />
            </Paragraph>
          </Card>
        </div>
      </Content>
    );
  }

  return (
    <Content style={{ margin: '0 16px' }}>
      <Breadcrumb style={{ margin: '16px 0' }}>
        <Breadcrumb.Item>RecogniX</Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'section.adminDashboardName'} defaultMessage="Admin Dashboard" />
        </Breadcrumb.Item>
        <Breadcrumb.Item onClick={() => navigate('/admin/events')} className="breadcrumb-link">
          <FormattedMessage id={'section.eventsName'} defaultMessage="Events" />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <FormattedMessage id={'admin.event.details'} defaultMessage="Event Details" />
        </Breadcrumb.Item>
      </Breadcrumb>
      
      <div
        style={{
          padding: 24,
          minHeight: '79vh',
          background: 'white',
          borderRadius: 2,
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/admin/events')}
          >
            <FormattedMessage id={'common.back'} defaultMessage="Back to Events" />
          </Button>
        </div>

        <Card 
          title={
            <Space>
              <Title level={2}>{event.name}</Title>
              <Tag color={getStatusColor(event.status)} style={{ marginLeft: 8 }}>
                {Utils.readableGeneralStatus(event.status)}
              </Tag>
            </Space>
          } 
          extra={
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              onClick={() => navigate(`/admin/events/${event.id}/edit`)}
            >
              <FormattedMessage id={'common.edit'} defaultMessage="Edit" />
            </Button>
          }
        >
          <Tabs activeKey={activeTab} onChange={handleTabChange}>
            <Tabs.TabPane 
              tab={
                <span>
                  <CalendarOutlined />
                  <FormattedMessage id={'event.tabs.details'} defaultMessage="Details" />
                </span>
              } 
              key="details"
            >
              <Descriptions layout="vertical" bordered column={{ xxl: 4, xl: 3, lg: 3, md: 3, sm: 2, xs: 1 }}>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.id' }) || "ID"}>
                  {event.id}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.date' }) || "Date"}>
                  {formatDate(event.date)}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.location' }) || "Location"}>
                  {event.location || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.status' }) || "Status"}>
                  <Tag color={getStatusColor(event.status)}>
                    {Utils.readableGeneralStatus(event.status)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.hash' }) || "Hash"} span={2}>
                  {event.hash}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.organisation' }) || "Organisation"} span={2}>
                  {event.organisation?.name || `Organisation ID: ${event.organisationId}`}
                </Descriptions.Item>
                <Descriptions.Item label={intl.formatMessage({ id: 'event.description' }) || "Description"} span={4}>
                  {event.description || intl.formatMessage({ id: 'event.noDescription' }) || "No description provided"}
                </Descriptions.Item>
              </Descriptions>
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <PictureOutlined />
                  <FormattedMessage id={'event.tabs.galleries'} defaultMessage="Galleries" />
                </span>
              } 
              key="galleries"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <FormattedMessage id={'admin.event.galleriesDescription'} defaultMessage="Manage galleries associated with this event." />
                </Paragraph>
                
                <Button 
                  type="primary" 
                  onClick={() => {
                    if (event.organisation && event.organisation.id) {
                      navigate(`/admin/organisations/${event.organisation.id}/events/${event.id}/galleries`);
                    } else {
                      navigate(`/admin/events/${event.id}/galleries`);
                    }
                  }}
                >
                  <FormattedMessage id={'admin.event.viewGalleries'} defaultMessage="View Galleries" />
                </Button>
              </Space>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <span>
                  <RobotOutlined />
                  <FormattedMessage id={'event.tabs.recognition'} defaultMessage="Photo Recognition" />
                </span>
              } 
              key="recognition"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <FormattedMessage 
                    id={'admin.event.recognitionDescription'} 
                    defaultMessage="Manage photo recognition indexing for this event. Users can search for photos by uploading selfies once indexing is complete." 
                  />
                </Paragraph>
                
                <RecognitionIndexingStatus
                  eventId={event.id}
                  eventName={event.name}
                />
              </Space>
            </Tabs.TabPane>

            <Tabs.TabPane 
              tab={
                <span>
                  <BarChartOutlined />
                  <FormattedMessage id={'event.tabs.analytics'} defaultMessage="Analytics" />
                </span>
              } 
              key="analytics"
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Paragraph>
                  <FormattedMessage 
                    id={'admin.event.analyticsDescription'} 
                    defaultMessage="View usage statistics and engagement metrics for this event." 
                  />
                </Paragraph>
                
                <EventAnalytics eventId={event.id} />
              </Space>
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </Content>
  );
};

export default AdminEventDetailPage;